import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AtSign, ChevronDown, Mic, MicOff, Slash, Sparkles, Telescope, Workflow, X } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, PromptInputButton, cn } from "@ncp/ui";
import type { AssistantSummary } from "../types";

export interface PlaybookOption { key: string; label: string; description?: string | null }

/** How the next message is handled: a plain question, a research hand-off to a specialist, or a request addressed to one specialist. */
export type ComposerMode = { kind: "ask" } | { kind: "research" } | { kind: "specialist"; key: string };

// ---------------------------------------------------------------- @specialist and /process completion

export interface CompletionItem { key: string; label: string; description?: string | null }
export interface Completion { kind: "mention" | "playbook"; query: string; start: number; end: number; items: CompletionItem[] }

/** `@` anywhere after whitespace completes a specialist; `/` at the very start completes a process. */
export function useCompletion(draft: string, assistant: AssistantSummary | null, playbooks: PlaybookOption[], locale: string): Completion | null {
  return useMemo(() => {
    const mention = /(^|\s)@([\w-]*)$/.exec(draft);
    if (mention) {
      const query = mention[2].toLowerCase();
      const start = draft.length - mention[2].length - 1;
      const items = (assistant?.delegates ?? [])
        .map((d) => ({ key: d.key, label: locale === "ar" && d.nameAr ? d.nameAr : d.name, description: locale === "ar" && d.purposeAr ? d.purposeAr : d.purpose }))
        .filter((d) => !query || d.key.toLowerCase().includes(query) || d.label.toLowerCase().includes(query))
        .slice(0, 8);
      return { kind: "mention", query, start, end: draft.length, items };
    }
    const playbook = /^\/([\w-]*)$/.exec(draft);
    if (playbook && playbooks.length > 0) {
      const query = playbook[1].toLowerCase();
      const items = playbooks.filter((p) => !query || p.key.toLowerCase().includes(query) || p.label.toLowerCase().includes(query)).slice(0, 8);
      return { kind: "playbook", query, start: 0, end: draft.length, items };
    }
    return null;
  }, [draft, assistant, playbooks, locale]);
}

function specialistLabel(assistant: AssistantSummary | null, key: string, locale: string): string {
  const d = assistant?.delegates.find((x) => x.key === key);
  return d ? (locale === "ar" && d.nameAr ? d.nameAr : d.name) : key;
}

/** The specialist a research hand-off goes to: the evidence researcher when the assistant has one. */
export function researcherOf(assistant: AssistantSummary | null): string | null {
  const delegates = assistant?.delegates ?? [];
  return (delegates.find((d) => d.key === "evidence-researcher") ?? delegates.find((d) => /research/i.test(d.key)))?.key ?? null;
}

/**
 * The orchestrator directive for a message: the composer's mode first, then a leading `@specialist` or `/process` token typed by hand.
 * The message itself is sent as typed.
 */
export function directiveFor(text: string, mode: ComposerMode, assistant: AssistantSummary | null, playbooks: PlaybookOption[], locale: string): string | undefined {
  if (mode.kind === "specialist") return `specialist AI assistant '${mode.key}' (${specialistLabel(assistant, mode.key, locale)}) – delegate this task to it`;
  if (mode.kind === "research") {
    const researcher = researcherOf(assistant);
    return researcher
      ? `specialist AI assistant '${researcher}' – research this in depth: plan the sub-questions, gather the evidence and return a cited synthesis`
      : "deep research – plan the sub-questions, gather the evidence for each and write a cited synthesis";
  }
  const mention = /(?:^|\s)@([\w-]+)/.exec(text);
  if (mention) {
    const d = assistant?.delegates.find((x) => x.key === mention[1]);
    if (d) return `specialist AI assistant '${d.key}' (${locale === "ar" && d.nameAr ? d.nameAr : d.name}) – delegate this task to it`;
  }
  const playbook = /^\/([\w-]+)/.exec(text);
  if (playbook) {
    const p = playbooks.find((x) => x.key === playbook[1]);
    if (p) return `playbook '${p.key}' (${p.label}) – start it for this request`;
  }
  return undefined;
}

export function CompletionMenu({ completion, activeIndex, onPick, onHover }: { completion: Completion; activeIndex: number; onPick: (item: CompletionItem) => void; onHover: (index: number) => void }) {
  const { t } = useTranslation();
  if (completion.items.length === 0) return <p className="mb-2 px-1 text-xs text-muted-foreground">{t("ai.chat.noMatches")}</p>;
  return (
    <ul role="listbox" className="mb-2 max-h-56 overflow-y-auto rounded-xl border border-sand-200 bg-white p-1 shadow-sand-lg scrollbar-thin">
      <li className="px-2 py-1 text-[11px] font-semibold text-sand-600">{completion.kind === "mention" ? t("ai.chat.askSpecialist") : t("ai.chat.startProcess")}</li>
      {completion.items.map((item, i) => (
        <li key={item.key} role="option" aria-selected={i === activeIndex}>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(item)} onMouseEnter={() => onHover(i)} className={cn("flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-start text-sm", i === activeIndex ? "bg-aegold-50 text-aegold-900" : "hover:bg-sand-50")}>
            <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-md bg-sand-100 text-sand-700">{completion.kind === "mention" ? <AtSign className="size-3.5" /> : <Slash className="size-3.5" />}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{item.label}</span>
              {item.description ? <span className="block truncate text-xs text-muted-foreground">{item.description}</span> : null}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- composer tools

/** The tools under the composer's text: start a process, address a specialist, and dictate. */
export function ComposerTools({ assistant, playbooks, locale, disabled, onStartProcess, onAskSpecialist, speech, compact = false }: {
  assistant: AssistantSummary | null;
  playbooks: PlaybookOption[];
  locale: string;
  disabled?: boolean;
  onStartProcess?: (playbookKey: string) => void;
  onAskSpecialist: (agentKey: string) => void;
  speech?: { supported: boolean; listening: boolean; toggle: () => void } | null;
  /** Icons only (the ECONIC inspector's composer bar, where the segmented controls take the room). */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const delegates = assistant?.delegates ?? [];
  return (
    <>
      {playbooks.length > 0 && onStartProcess ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PromptInputButton disabled={disabled} aria-label={compact ? t("ai.chat.startProcess") : undefined} title={compact ? t("ai.chat.startProcess") : undefined} className={compact ? "ea-icon" : undefined}><Workflow />{compact ? null : <><span className="hidden sm:inline">{t("ai.chat.startProcess")}</span><ChevronDown className="!size-3 opacity-60" /></>}</PromptInputButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80">
            <DropdownMenuLabel className="normal-case tracking-normal">{t("ai.chat.startProcessHint")}</DropdownMenuLabel>
            {playbooks.map((p) => (
              <DropdownMenuItem key={p.key} onSelect={() => onStartProcess(p.key)} className="items-start">
                <Workflow className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{p.label}</span>
                  {p.description ? <span className="line-clamp-2 block text-xs text-muted-foreground">{p.description}</span> : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {delegates.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <PromptInputButton disabled={disabled} aria-label={compact ? t("ai.chat.askSpecialist") : undefined} title={compact ? t("ai.chat.askSpecialist") : undefined} className={compact ? "ea-icon" : undefined}><AtSign />{compact ? null : <><span className="hidden sm:inline">{t("ai.chat.askSpecialist")}</span><ChevronDown className="!size-3 opacity-60" /></>}</PromptInputButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-96 w-80 overflow-y-auto">
            <DropdownMenuLabel className="normal-case tracking-normal">{t("ai.chat.askSpecialistHint")}</DropdownMenuLabel>
            {delegates.map((d) => (
              <DropdownMenuItem key={d.key} onSelect={() => onAskSpecialist(d.key)} className="items-start">
                <Sparkles className="mt-0.5" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{locale === "ar" && d.nameAr ? d.nameAr : d.name}</span>
                  {d.purpose ? <span className="line-clamp-2 block text-xs text-muted-foreground">{locale === "ar" && d.purposeAr ? d.purposeAr : d.purpose}</span> : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {speech?.supported ? (
        <PromptInputButton tooltip={speech.listening ? t("ai.chat.listening") : t("ai.chat.voice")} aria-label={t("ai.chat.voice")} active={speech.listening} className={cn(speech.listening && "animate-pulse")} onClick={speech.toggle} disabled={disabled}>
          {speech.listening ? <MicOff /> : <Mic />}
        </PromptInputButton>
      ) : null}
    </>
  );
}

/** The mode the next message goes out in, shown inside the composer with a way back to a plain question. */
export function ModeChip({ mode, assistant, locale, onClear }: { mode: ComposerMode; assistant: AssistantSummary | null; locale: string; onClear: () => void }) {
  const { t } = useTranslation();
  if (mode.kind === "ask") return null;
  const label = mode.kind === "research" ? t("ai.chat.modes.research") : t("ai.chat.toSpecialist", { agent: specialistLabel(assistant, mode.key, locale) });
  return (
    <div className="px-3 pt-3">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-seablue-200 bg-seablue-50 py-0.5 pe-1 ps-2 text-xs font-medium text-seablue-900">
        {mode.kind === "research" ? <Telescope className="size-3.5" /> : <AtSign className="size-3.5" />}
        {label}
        <button type="button" onClick={onClear} aria-label={t("ai.chat.clearMode")} title={t("ai.chat.clearMode")} className="rounded-full p-0.5 hover:bg-seablue-100"><X className="size-3" /></button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- voice

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

function speechCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Browser speech recognition; the server speech-to-text route replaces it later. */
export function useSpeechInput(lang: string, onText: (text: string) => void) {
  const supported = speechCtor() !== null;
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;
  useEffect(() => () => recRef.current?.stop(), []);
  const toggle = useCallback(() => {
    if (recRef.current) { recRef.current.stop(); recRef.current = null; setListening(false); return; }
    const Ctor = speechCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal && r[0]) onTextRef.current(r[0].transcript.trim());
      }
    };
    rec.onend = () => { recRef.current = null; setListening(false); };
    rec.onerror = () => { recRef.current = null; setListening(false); };
    recRef.current = rec;
    setListening(true);
    rec.start();
  }, [lang]);
  return { supported, listening, toggle };
}
